import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FlexColumn } from 'ui-components/components/flex';

import { FeatureCardCollection } from './feature-cards/feature-cards';
import { FTU_WIZARD_PATH } from '../../../routes/constants';
import { isScreenMobile } from '../../../constants/screen-width-breakpoints';
import { WorkflowSteps, useTrackWorkflow } from '../../../tracking/use-workflow-tracking';
import { Workflows as workflows } from '../../../tracking/tracking-constants';
import { useFreemiumVisibility } from './hooks/use-freemium-visibility';
import { useShippingVolumeCategory } from '../../../hooks/use-shipping-volume-category';
import { PlanUpsellCarousel } from './plan-upsell-carousel';
import { ScrollToAnchor } from '../../../components/scroll-to-anchor';
import { SetUpBanner } from './set-up-banner/set-up-banner';
import { SetupCompleteBanner } from './setup-complete-banner';
import { ShippingRateCalculator } from './shipping-rate-calculator';
import { FreemiumShippingRateCalculator } from './freemium-shipping-rate-calculator';
import { SingleLabelShippingRateCalculator } from './single-label-shipping-rate-calculator';
import { getStepperLabels } from './ftu-wizard/utils/get-wizard-steps';
import { TodoList } from './todo-cards/todo-list';
import { useEnablePhoneNumberCapture } from '../../../feature-flags/hooks/use-enable-phone-number-capture';
import { useFTUWizardContext } from './ftu-wizard/context/ftu-wizard-context';
import { useWizardData } from './ftu-wizard/hooks/use-wizard-data';
import { MigratePlanBanner } from '../../settings/payment-and-subscription/components/migrate-plan-modal/migration-banner';
import Global from '../../../store/global-selectors';
import style from './style.module.less';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag';
import { FeatureFlagDeclarations } from '../../../feature-flags/feature-flag-declarations';
import { useEnableSupplyPage } from '../../../feature-flags/hooks/use-enable-supply-page';
import { SuppliesCardContainer } from './supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from './supplies-cards/supplies-page-constants';
import { OperationsCards } from './operations-cards/operations-cards';
import { useEnableOperationsCards } from '../../../feature-flags/hooks/use-enable-operations-cards';

const HOME_PAGE = 'home-page';

export const HomePage2 = () => {
  const history = useHistory();
  const isMobile = isScreenMobile();
  const { shouldShowFreemiumComponents, isTrialActive } = useFreemiumVisibility();
  const { getInitialStep, isSetupWizardComplete, triggerAutoInjection } = useFTUWizardContext();
  const shouldDisplayFTUFlow = isTrialActive;
  const initialStep = getInitialStep();
  const trackWorkflow = useTrackWorkflow(workflows.FTU_WIZARD_FLOW);
  const metadata = useWizardData();
  const currentStepKey = getInitialStep();
  const shouldShowFreemiumCalculator = shouldShowFreemiumComponents;
  const { isSmallTrialer } = useShippingVolumeCategory();
  const referralCode = useSelector(Global.selectSellerReferralCode);
  const isExperimentEnabled = useFeatureFlag(FeatureFlagDeclarations.enableSingleLabelSellerExperiment);
  const enableSupplyPage = useEnableSupplyPage();
  const isPhoneNumberCaptureEnabled = useEnablePhoneNumberCapture();
  const enableOperationsCards = useEnableOperationsCards();
  const sellerPhoneNumber = useSelector(Global.selectSellerPhoneNumber);

  const isReferralCodeMatch = referralCode?.length > 0 && referralCode.toLowerCase().includes('ppc');
  const shouldShowSingleLabelExperience = isSmallTrialer && isReferralCodeMatch && isExperimentEnabled;

  // Only show phone step if feature is enabled AND seller doesn't have a phone number
  const hasPhoneNumber = Boolean(sellerPhoneNumber);
  const shouldIncludePhoneStep = isPhoneNumberCaptureEnabled && !hasPhoneNumber;
  const stepperLabels = getStepperLabels(shouldIncludePhoneStep);

  const onOpenDrawer = useCallback(() => {
    if (!shouldDisplayFTUFlow) {
      return;
    }

    triggerAutoInjection();

    trackWorkflow(WorkflowSteps.START, {
      metadata,
      initialPage: initialStep,
      currentPage: currentStepKey,
      source: HOME_PAGE,
    });

    history.push(FTU_WIZARD_PATH);
  }, [shouldDisplayFTUFlow, triggerAutoInjection, trackWorkflow, metadata, initialStep, currentStepKey, history]);

  const renderCalculator = () => {
    // Priority order: Single label experience takes precedence over freemium calculator
    if (shouldShowSingleLabelExperience) {
      return <SingleLabelShippingRateCalculator />;
    }
    if (shouldShowFreemiumCalculator) {
      return <FreemiumShippingRateCalculator />;
    }
    return <ShippingRateCalculator />;
  };

  return (
    <FlexColumn className={style['home-page']}>
      <ScrollToAnchor />
      {!isMobile && renderCalculator()}
      <FlexColumn className={style.content}>
        <MigratePlanBanner className={style['migration-banner']} />
        {!shouldShowSingleLabelExperience && shouldDisplayFTUFlow && (
          <div className={style['set-up-banner-wrapper']}>
            {isSetupWizardComplete && <SetupCompleteBanner />}
            <SetUpBanner isSetupWizardComplete={isSetupWizardComplete} openDrawer={onOpenDrawer} stepLabels={stepperLabels} />
          </div>
        )}
        {enableOperationsCards && <OperationsCards />}
        {!shouldShowSingleLabelExperience && <TodoList />}
        {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.HOME} />}
        {shouldShowFreemiumComponents && <PlanUpsellCarousel />}
        {!shouldShowSingleLabelExperience && <FeatureCardCollection />}
      </FlexColumn>
    </FlexColumn>
  );
};
